import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import api, { formatApiError } from "../lib/api";
import { Package, ChevronRight, ShoppingBag, Ban } from "lucide-react";
import { toast } from "sonner";
import CancellationTimer, { isCancellable } from "../components/CancellationTimer";
import ConfirmModal from "../components/ConfirmModal";
import SEOHead from "../components/SEOHead";

const STATUS_PILL = {
  placed:           "bg-brand-turmeric/15 text-brand-turmeric border-brand-turmeric/30",
  confirmed:        "bg-brand-turmeric/15 text-brand-turmeric border-brand-turmeric/30",
  packed:           "bg-brand-turmeric/15 text-brand-turmeric border-brand-turmeric/30",
  shipped:          "bg-brand-chili/10 text-brand-chili border-brand-chili/30",
  delivered:        "bg-brand-success/10 text-brand-success border-brand-success/30",
  cancelled:        "bg-brand-subtle text-brand-muted border-brand-line",
  returned:         "bg-brand-subtle text-brand-muted border-brand-line",
  // iter39 — return & refund states
  return_requested: "bg-amber-50 text-amber-900 border-amber-200",
  return_approved:  "bg-emerald-50 text-emerald-900 border-emerald-200",
  return_rejected:  "bg-red-50 text-red-900 border-red-200",
  refund_initiated: "bg-blue-50 text-blue-900 border-blue-200",
  refund_completed: "bg-emerald-100 text-emerald-900 border-emerald-300",
};

const STATUS_LABEL = {
  placed:           "placed",
  confirmed:        "confirmed",
  packed:           "packed",
  shipped:          "shipped",
  delivered:        "delivered",
  cancelled:        "cancelled",
  returned:         "returned",
  return_requested: "Return Requested",
  return_approved:  "Return Approved",
  return_rejected:  "Return Rejected",
  refund_initiated: "Refund Initiated",
  refund_completed: "Refund Completed",
};

const PAYMENT_PILL = {
  paid:             "text-brand-success",
  pending:          "text-brand-turmeric",
  cod:              "text-brand-turmeric",
  failed:           "text-brand-chili",
  signature_failed: "text-brand-chili",
  cancelled:        "text-brand-muted",
};

export default function Orders() {
  const { user, loading } = useAuth();
  const { orderCancellationWindowEnabled, customerCancellationEnabled } = useSettings();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(true);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login?redirect=/orders");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    setBusy(true);
    api.get("/orders")
      .then((r) => setOrders(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [user]);

  const requestCancel = useCallback((order) => {
    setCancelTarget(order);
  }, []);

  const confirmCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/orders/${cancelTarget.order_id}/cancel`);
      setOrders((prev) => prev.map((o) => (o.order_id === data.order_id ? data : o)));
      toast.success("Order cancelled successfully.");
      setCancelTarget(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCancelling(false);
    }
  }, [cancelTarget]);

  if (!user) return null;

  return (
    <div className="container-x py-12 lg:py-20 max-w-5xl" data-testid="orders-page">
      <SEOHead title="Your Orders | Sri Durga Foods" description="Track and review your Sri Durga Foods orders — real-time status, invoices, and return options." noindex />
      {/* Heading — matches Account's "MY ACCOUNT" exactly */}
      <div className="mb-10">
        <span className="overline mb-3 block" data-testid="orders-overline">My Pantry</span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-dark" data-testid="orders-heading">
          Your orders.
        </h1>
        <p className="text-sm text-brand-muted mt-3">
          Everything you've ordered from Sri Durga Foods — placed, in transit and delivered.
        </p>
      </div>

      {busy ? (
        <div className="text-center py-24 text-brand-muted" data-testid="orders-loading">Loading your orders…</div>
      ) : orders.length === 0 ? (
        <div
          className="text-center py-20 bg-white border border-brand-line rounded-2xl"
          data-testid="no-orders"
        >
          <Package className="w-10 h-10 mx-auto text-brand-muted mb-4" strokeWidth={1.3} />
          <p className="text-brand-muted mb-6">You haven't placed any orders yet.</p>
          <Link to="/shop" className="btn-primary" data-testid="start-shopping">
            <ShoppingBag className="w-4 h-4 mr-2" strokeWidth={2} /> Start shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <OrderRow
              key={o.order_id}
              order={o}
              onCancelRequest={requestCancel}
              windowEnabled={orderCancellationWindowEnabled}
              customerCancellationEnabled={customerCancellationEnabled}
            />
          ))}
        </div>
      )}

      <ConfirmModal
        open={!!cancelTarget}
        title="Cancel this order?"
        message="Are you sure you want to cancel this order? This cannot be undone."
        confirmLabel={cancelling ? "Cancelling…" : "Yes, cancel order"}
        cancelLabel="Keep order"
        confirmDisabled={cancelling}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancel}
        dataTestId="orders-cancel-confirm"
      />
    </div>
  );
}

function OrderRow({ order, onCancelRequest, windowEnabled, customerCancellationEnabled }) {
  const [eligible, setEligible] = useState(() => isCancellable(order, 30, windowEnabled));
  // When the 30-minute window is OFF we don't need a per-second poller — the
  // status alone determines eligibility and is refreshed on the next refetch.
  useEffect(() => {
    if (!windowEnabled) { setEligible(isCancellable(order, 30, false)); return undefined; }
    if (!eligible) return undefined;
    const id = setInterval(() => {
      setEligible(isCancellable(order, 30, true));
    }, 1000);
    return () => clearInterval(id);
  }, [order, eligible, windowEnabled]);

  const thumbs = order.items.slice(0, 3);
  const extra = Math.max(0, order.items.length - 3);
  const dt = order.created_at ? new Date(order.created_at) : null;

  return (
    <div
      className="group block bg-white border border-brand-line rounded-2xl p-5 sm:p-6 hover:border-brand-chili/30 hover:shadow-[0_8px_24px_-12px_rgba(46,16,16,0.18)] transition-all"
      data-testid={`order-row-${order.order_id}`}
    >
      <Link to={`/orders/${order.order_id}`} className="block">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <div className="font-mono text-[11px] tracking-wider text-brand-muted uppercase mb-1">
              {order.order_id}
            </div>
            <div className="font-serif text-xl text-brand-dark">
              {order.items.length} item{order.items.length !== 1 ? "s" : ""}
            </div>
            <div className="text-xs text-brand-muted mt-1">
              {dt ? dt.toLocaleString("en-IN", {
                day: "2-digit",
                month: "long",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
                timeZone: "Asia/Kolkata",
              }) : ""}
            </div>
          </div>
          <div className="text-right">
            <div className="font-semibold text-brand-dark text-lg">₹{order.total.toFixed(0)}</div>
            <div className={`text-[10px] uppercase tracking-wider mt-1 ${PAYMENT_PILL[order.payment_status] || "text-brand-muted"}`}>
              {order.payment_status === "paid" ? "Paid online" : (order.payment_method === "cod" ? "Cash on Delivery" : order.payment_status)}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-4">
          {thumbs.map((it, idx) => (
            <div
              key={`${it.product_id || it.name || "thumb"}-${idx}`}
              className="w-14 h-14 rounded-xl border border-brand-line bg-brand-cream overflow-hidden"
              title={it.name}
            >
              {it.image ? (
                <img src={it.image} alt="" className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package className="w-5 h-5 text-brand-muted" strokeWidth={1.3} />
                </div>
              )}
            </div>
          ))}
          {extra > 0 && (
            <div className="w-14 h-14 rounded-xl bg-brand-subtle text-brand-dark text-xs font-medium flex items-center justify-center">
              +{extra}
            </div>
          )}
          <div className="ml-1 text-sm text-brand-muted truncate max-w-[40%]">
            {thumbs.map((t) => t.name).join(", ")}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${STATUS_PILL[order.order_status] || STATUS_PILL.placed}`}
          >
            {STATUS_LABEL[order.order_status] || order.order_status}
          </span>
          <span className="text-xs text-brand-muted inline-flex items-center gap-1.5 group-hover:gap-2 transition-all">
            View details <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.8} />
          </span>
        </div>
      </Link>

      {/* Inline cancellation control — visible while the order is still
          cancellable AND the admin has enabled customer self-cancellation
          (iter96 / Session C). */}
      {eligible && customerCancellationEnabled && (
        <div
          className="mt-4 pt-4 border-t border-brand-line flex flex-wrap items-center justify-between gap-3"
          data-testid={`order-cancel-row-${order.order_id}`}
        >
          <div className="text-xs text-brand-muted">
            {windowEnabled ? (
              <>
                Cancel available:{" "}
                <CancellationTimer
                  createdAt={order.created_at}
                  onExpire={() => setEligible(false)}
                  testId={`order-cancel-timer-${order.order_id}`}
                />
              </>
            ) : (
              <>You can cancel this order before dispatch.</>
            )}
          </div>
          <button
            type="button"
            onClick={() => onCancelRequest(order)}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-chili border border-brand-chili rounded-full px-3 py-1.5 active:bg-brand-chili/5 [@media(hover:hover)]:hover:bg-brand-chili/5 transition-colors"
            data-testid={`order-cancel-btn-${order.order_id}`}
          >
            <Ban className="w-3.5 h-3.5" strokeWidth={1.8} />
            Cancel order
          </button>
        </div>
      )}
    </div>
  );
}
